import echarts from "echarts";

const VIBRANT_COLORS = [
  "#3F51B5",
  "#795548",
  "#8BC34A",
  "#009688",
  "#9C27B0",
  "#2196F3",
  "#4b6928",
  "#F44336",
  "#FF9800",
  "#E91E63",
  "#FFC107",
];

const PASTEL_COLORS = [
  "#8498e6",
  "#a88e84",
  "#a5b98d",
  "#83c2bc",
  "#b07eb9",
  "#a4a8c2",
  "#9acb9a",
  "#FCBAD3",
  "#B5EAD7",
  "#D4A5A5",
  "#FFD3B6",
  "#AA96DA",
  "#E2F0CB",
  "#FFDAC1",
  "#FFC107",
  "#A8D8EA",
];

const EMPTY_MESSAGE = [
  {
    type: "text",
    left: "center",
    top: "middle",
    silent: true,
    invisible: false,
    id: "empty-message",
    style: {
      fill: "#686868",
      text: "Selecione um ou mais itens na legenda acima para visualizar",
      font: "14px sans-serif",
    },
  },
];

const CROSS_TOOLTIP = {
  trigger: "axis",
  axisPointer: {
    type: "cross",
    crossStyle: { color: "#000000" },
  },
  formatter(params) {
    const tooltipParams = Array.isArray(params) ? params : [params];
    if (!tooltipParams.length) return "";

    const title =
      tooltipParams[0].axisValueLabel || tooltipParams[0].name || "";
    const items = tooltipParams
      .map((item) => {
        const formattedValue = formatValueBySeriesName(
          item.seriesName,
          item.value,
        );

        return `${item.marker}${item.seriesName}: ${formattedValue}`;
      })
      .join("<br/>");

    return `${title}<br/>${items}`;
  },
};

const PIE_TOOLTIP = {
  trigger: "item",
  formatter(params) {
    const formattedValue = formatValueBySeriesName(
      params.seriesName,
      params.value,
    );
    const formattedPercent = percentIntl.format(params.percent || 0);

    return `${params.name} : ${formattedValue} (${formattedPercent}%)`;
  },
};

const chartLocale =
  typeof navigator !== "undefined" && navigator.language
    ? navigator.language
    : "pt-BR";

const numberIntl = new Intl.NumberFormat(chartLocale, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const percentIntl = new Intl.NumberFormat(chartLocale, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const currencyIntl = new Intl.NumberFormat(chartLocale, {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const getNumericValue = (params) => {
  if (params == null) return 0;
  if (typeof params === "number") return params;
  if (typeof params === "string") {
    const parsed = Number(params);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const value = params.value;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const defaultFormatter = (params) => {
  return numberIntl.format(getNumericValue(params));
};

const percFormatter = (params) => {
  return `${percentIntl.format(getNumericValue(params))} %`;
};

const moneyFormatter = (params) => {
  return currencyIntl.format(getNumericValue(params));
};

const percentageSeriesNames = [
  "Taxa de Interesse",
  "Taxa de Login",
  "Taxa de Conversão",
  "Taxa de Implantação",
  "Taxa de implantação",
  "Efic",
  "Taxa",
];

const currencySeriesNames = ["CPP", "CPL", "Custo"];

function formatValueBySeriesName(seriesName, value) {
  const formatterParams = { value };

  if (currencySeriesNames.includes(seriesName)) {
    return moneyFormatter(formatterParams);
  }

  if (percentageSeriesNames.includes(seriesName)) {
    return percFormatter(formatterParams);
  }

  return defaultFormatter(formatterParams);
}

function createYAxis(position, formatter = defaultFormatter, options) {
  return {
    type: "value",
    position,
    axisLabel: { formatter },
    axisPointer: {
      label: {
        formatter,
      },
    },
    splitLine: {
      show: false,
    },
    show: false,
    ...options,
  };
}

function createSeries(
  type,
  name,
  colorIndex,
  formatter = defaultFormatter,
  options,
) {
  const palette = type === "bar" ? PASTEL_COLORS : VIBRANT_COLORS;
  return {
    name,
    type,
    data: [],
    barCategoryGap: "0%",
    barGap: "0%",
    label: {
      show: false,
      position: "top", // 'top', 'inside', 'insideTop', 'insideBottom', ...
      color: "#333",
      fontSize: 10,
      fontWeight: "bold",
      textShadowColor: "#ffffff",
      textShadowBlur: 2,
      textShadowOffsetX: 0,
      textShadowOffsetY: 0,
      formatter,
    },
    color: palette[colorIndex % palette.length],
    ...options,
  };
}

function createPieOptions(seriesName) {
  return {
    tooltip: PIE_TOOLTIP,
    legend: {
      orient: "vertical",
      left: "left",
      top: "middle",

      textStyle: {
        rich: {
          name: {
            width: 90,
            align: "left",
          },
          percent: {
            align: "right",
          },
        },
      },
    },
    series: [
      {
        name: seriesName,
        type: "pie",
        radius: "70%",
        position: "inside",
        center: ["60%", "50%"],
        data: [],
        startAngle: 20,
        label: { show: false, position: "center" },
        labelLine: { show: false },
      },
    ],
  };
}

function createNotificationVBarOptions() {
  const barNames = [
    // [0]
    "Notificação",
    // [1]
    "Acessos",
    // [2]
    "Logins",
    // [3]
    "Confirmados",
    // [4]
    "Aceitos",
    // [5]
    "Entregues",
    // [6]
    "Visualizados",
  ];
  const lineNames = [
    // [7]
    "Taxa de Interesse",
    // [8]
    "Taxa de Login",
    // [9]
    "Taxa de Conversão",
    // [10]
    "Taxa de Implantação",
    // [11]
    "Taxa de Entrega",
    // [12]
    "Taxa de Visualização",
  ];

  const costNames = [
    // [13]
    "CPP",
    // [14]
    "CPL",
  ];

  const series = []
    .concat(
      barNames.map(function (name, i) {
        return createSeries("bar", name, i, defaultFormatter, {
          yAxisIndex: 0,
        });
      }),
    )
    .concat(
      lineNames.map(function (name, i) {
        return createSeries("line", name, i + 1, percFormatter, {
          yAxisIndex: 1,
        });
      }),
    )
    .concat(
      costNames.map(function (name, i) {
        return createSeries(
          "line",
          name,
          i + lineNames.length + 1,
          moneyFormatter,
          {
            yAxisIndex: 2,
          },
        );
      }),
    );

  const legend = {
    data: barNames.concat(lineNames, costNames),
    selected: {},
  };

  barNames.concat(lineNames, costNames).forEach((name) => {
    legend.selected[name] = false;
  });

  return {
    tooltip: CROSS_TOOLTIP,
    legend: legend,
    graphic: EMPTY_MESSAGE,

    grid: {
      left: "0%",
      right: "0%",
      bottom: "0%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      axisTick: { show: true, alignWithLabel: true },
      axisLabel: {
        interval: 0, // Show all labels
      },
      data: [],
    },
    yAxis: [
      createYAxis("left", defaultFormatter),
      createYAxis("right", percFormatter),
      createYAxis("right", moneyFormatter, { offset: 50 }),
    ],
    series,
  };
}

export default {
  periodOptionsBar: {
    tooltip: CROSS_TOOLTIP,
    title: { show: false },
    grid: {
      right: "0%",
      left: "0%",
      bottom: "0%",
      containLabel: true,
    },
    legend: {
      left: "6%",
      top: "0%",
      data: [
        "Acessos",
        "Localizados",
        "Confirmados",
        "Aceitos",
        "Entregues",
        "Visualizados",
        "Notificações",
        "CPP",
        "CPL",
        "Taxa de implantação",
        "Taxa de Conversão",
        "Taxa de Interesse",
        "Taxa de Entrega",
        "Taxa de Visualização",
      ],
      selected: {
        Acessos: false,
        Localizados: false,
        Confirmados: false,
        Aceitos: false,
        Entregues: false,
        Visualizados: false,
        Notificações: false,
        CPP: false,
        CPL: false,
        "Taxa de implantação": false,
        "Taxa de Conversão": false,
        "Taxa de Interesse": false,
        "Taxa de Entrega": false,
        "Taxa de Visualização": false,
      },
    },
    graphic: EMPTY_MESSAGE,
    xAxis: [
      {
        type: "category",
        axisTick: { show: true, alignWithLabel: true },
        data: [],
        width: "100%",
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: "rgb(255, 158, 68)" },
          { offset: 1, color: "rgb(255, 70, 131)" },
        ]),
        axisLabel: {},
      },
    ],
    yAxis: [
      createYAxis("left", defaultFormatter, { name: "Eventos" }),
      createYAxis("right", defaultFormatter, {
        name: "Notificações",
        axisLine: { lineStyle: { color: VIBRANT_COLORS[0] } },
      }),
      createYAxis("right", moneyFormatter, {
        name: "Custo",
        offset: 80,
        axisLine: { lineStyle: { color: VIBRANT_COLORS[9] } },
      }),
      createYAxis("right", percFormatter, {
        name: "Taxa",
        offset: 150,
        axisLine: { lineStyle: { color: VIBRANT_COLORS[4] } },
      }),
      createYAxis("right", percFormatter, {
        name: "Interesse",
        offset: 220,
        axisLine: { lineStyle: { color: VIBRANT_COLORS[1] } },
      }),
    ],
    series: [
      // [0]
      createSeries("bar", "Acessos", 1, defaultFormatter, {
        yAxisIndex: 0,
        barGap: 0,
        barWidth: "10%",
      }),
      // [1]
      createSeries("bar", "Localizados", 2, defaultFormatter, {
        yAxisIndex: 0,
        barGap: 0,
        barWidth: "10%",
      }),
      // [2]
      createSeries("bar", "Confirmados", 3, defaultFormatter, {
        yAxisIndex: 0,
        barGap: 0,
        barWidth: "10%",
      }),
      // [3]
      createSeries("bar", "Aceitos", 4, defaultFormatter, {
        yAxisIndex: 0,
        barGap: 0,
        barWidth: "10%",
      }),
      // [4]
      createSeries("bar", "Entregues", 5, defaultFormatter, {
        yAxisIndex: 0,
        barGap: 0,
        barWidth: "10%",
      }),
      // [5]
      createSeries("bar", "Visualizados", 6, defaultFormatter, {
        yAxisIndex: 0,
        barGap: 0,
        barWidth: "10%",
      }),
      // [6]
      createSeries("line", "Notificações", 0, defaultFormatter, {
        // symbolSize: 8,
        // symbol: "circle",
        yAxisIndex: 1,
        areaStyle: { opacity: 0.3 },
        lineStyle: { width: 0 },
      }),
      // [7]
      createSeries("line", "CPP", 7, moneyFormatter, {
        // symbolSize: 10,
        // symbol: "diamond",
        yAxisIndex: 2,
        lineStyle: { type: "" },
      }),
      // [8]
      createSeries("line", "CPL", 8, moneyFormatter, {
        // symbolSize: 10,
        // symbol: "diamond",
        yAxisIndex: 2,
        lineStyle: { type: "" },
      }),
      // [9]
      createSeries("line", "Taxa de implantação", 4, percFormatter, {
        // symbolSize: 10,
        // symbol: "pin",
        yAxisIndex: 3,
      }),
      // [10]
      createSeries("line", "Taxa de Conversão", 3, percFormatter, {
        // symbolSize: 10,
        // symbol: "pin",
        yAxisIndex: 3,
      }),
      // [11]
      createSeries("line", "Taxa de Interesse", 1, percFormatter, {
        // symbolSize: 10,
        // symbol: "pin",
        yAxisIndex: 4,
      }),
      // [12]
      createSeries("line", "Taxa de Entrega", 5, percFormatter, {
        // symbolSize: 10,
        // symbol: "pin",
        yAxisIndex: 3,
      }),
      // [13]
      createSeries("line", "Taxa de Visualização", 6, percFormatter, {
        // symbolSize: 10,
        // symbol: "pin",
        yAxisIndex: 3,
      }),
    ],
  },
  notificationCreditorsPie: createPieOptions("Notificações por Credor"),
  notificationApproachPie: createPieOptions("Notificações por Canal"),
  notificationPublicsPie: createPieOptions("Notificações por Público"),
  notificationGatewayPie: createPieOptions("Notificações por Gateway"),
  notificationCreditorsVBar: {
    ...createNotificationVBarOptions(),
    xAxis: {
      ...createNotificationVBarOptions().xAxis,
      axisLabel: {
        ...createNotificationVBarOptions().xAxis.axisLabel,
        rotate: 45,
      },
    },
  },
  notificationGatewayVBar: {
    ...createNotificationVBarOptions(),
    xAxis: {
      ...createNotificationVBarOptions().xAxis,
      axisLabel: {
        ...createNotificationVBarOptions().xAxis.axisLabel,
        rotate: 45,
      },
    },
  },
  notificationApproachVBar: createNotificationVBarOptions(),
  notificationPublicsVBar: createNotificationVBarOptions(),
};
